import { IconCheck, IconX, IconHome, IconTools, IconCircleX, IconCancel } from '@tabler/icons-react';
import clsx from 'clsx';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { ChangeRoomConditionSchema, ChangeRoomConditionSchemaType } from '../../../services/housekeeping/schema/ChangeRoomConditionSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { useChangeRoomCondition } from '../../../services/housekeeping/hooks/useChangeRoomCondition';
import { useAuth } from '../../../auth/context/AuthProvider';

const roomConditionIconMap: Record<string, { icon: JSX.Element; badgeColor: string }> = {
    ready: { icon: <IconCheck size={18} className='text-white' />, badgeColor: 'bg-success' },
    clean: { icon: <IconHome size={18} className='text-white' />, badgeColor: 'bg-primary' },
    cleaning_in_progress: { icon: <IconTools size={18} className='text-white' />, badgeColor: 'bg-warning' },
    dirty: { icon: <IconX size={18} className='text-white' />, badgeColor: 'bg-danger' },
    out_of_order: { icon: <IconCircleX size={18} className='text-white' />, badgeColor: 'bg-secondary' },
    do_not_disturb: { icon: <IconX size={18} className='text-white' />, badgeColor: 'bg-info' },
};

const renderTooltip = (
    control: any,
    handleSubmit: any,
    onSubmit: (data: any) => void,
    isPending: boolean
) => (props: any) => (
    <Tooltip id="tooltip-room-condition" {...props} className="tooltip-inner p-3">
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="d-flex align-items-center gap-2">
                <Controller
                    name="room_condition"
                    control={control}
                    render={({ field }) => (
                        <select {...field} className="form-select form-select-sm">
                            <option value="ready">Ready</option>
                            <option value="clean">Clean</option>
                            <option value="cleaning_in_progress">Cleaning In Progress</option>
                            <option value="dirty">Dirty</option>
                            <option value="out_of_order">Out of Order</option>
                            <option value="do_not_disturb">Do Not Disturb</option>
                        </select>
                    )}
                />
                <button
                    type="button"
                    className="btn btn-xs btn-outline-light d-flex align-items-center justify-content-center"
                    style={{ width: '24px', height: '24px', padding: '4px' }}
                    onClick={() => document.body.click()} // trigger rootClose
                >
                    <IconCancel size={12} />
                </button>

                <button
                    type="submit"
                    className="btn btn-xs btn-outline-success d-flex align-items-center justify-content-center"
                    style={{ width: '24px', height: '24px', padding: '4px' }}
                    disabled={isPending}
                >
                    <IconCheck size={12} />
                </button>
            </div>
        </form>
    </Tooltip>
);


const RoomConditionWithBadge: React.FC<{ room_condition: string, id: string }> = ({ room_condition, id }) => {
    const { user } = useAuth();

    const condition = room_condition.toLowerCase();
    const { icon, badgeColor } = roomConditionIconMap[condition] || { icon: null, badgeColor: '' };

    const { control, handleSubmit } = useForm<ChangeRoomConditionSchemaType>({
        resolver: zodResolver(ChangeRoomConditionSchema),
        defaultValues: {
            room_condition: condition
        },
    });

    const { mutate, isPending } = useChangeRoomCondition();
    const queryClient = useQueryClient();

    const onSubmit = useCallback((values: ChangeRoomConditionSchemaType) => {
        toast.loading('Updating room condition...', { id: 'update-room-condition' });

        mutate({
            housekeeping_id: id, data: { room_condition: values.room_condition, user_id: user?.id }
        }, {
            onSuccess: async () => {
                // hide overlay trigger
                document.body.click();

                await queryClient.refetchQueries();
                toast.success('Room condition updated successfully', { id: 'update-room-condition' });
            },
            onError: () => {
                toast.error('Failed to update room condition', { id: 'update-room-condition' });
            },
        });
    }, [mutate]);

    return (
        <OverlayTrigger
            placement="top"
            overlay={renderTooltip(control, handleSubmit, onSubmit, isPending)}
            trigger="click"
            rootClose
        >
            <div
                className="d-flex flex-column flex-md-row align-items-center justify-content-center text-center mx-auto"
                style={{ cursor: 'pointer' }}
            >
                <span className="d-block d-md-inline">
                    {room_condition
                        .split('_')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ')}
                </span>
                <span
                    className={clsx('badge', 'rounded-circle', 'ms-2', badgeColor)}
                    style={{
                        width: '24px',
                        height: '24px',
                        display: 'inline-flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {icon}
                </span>
            </div>
        </OverlayTrigger>
    );
};

export default RoomConditionWithBadge;